import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SocialShare from '../../components/feature/SocialShare';
import { blogPosts } from '../../mocks/blog';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAdBanner, setShowAdBanner] = useState(true);
  const navigate = useNavigate();
  const categories = ['All', 'Insights', 'Tips', 'Stories'];
  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter((p) => p.category === selectedCategory);

  return (
    <>
      <Header />

      {/* Floating Advertisement Banner */}
      {showAdBanner && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-slide-up">
          <div className="bg-gradient-to-br from-primary via-purple-600 to-accent-green rounded-2xl shadow-2xl overflow-hidden">
            <button
              onClick={() => setShowAdBanner(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line"></i>
            </button>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-white text-xs font-bold uppercase tracking-wider">Hot Deal</span>
              </div>
              
              <h3 className="text-white font-bold text-xl mb-2">
                🔥 Limited Time Offers!
              </h3>
              
              <div className="space-y-3 mb-4">
                <div 
                  onClick={() => {
                    navigate('/products');
                    setShowAdBanner(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-macbook-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">MacBook Pro 16"</p>
                      <p className="text-white/80 text-xs">Save $500 Today!</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  onClick={() => {
                    navigate('/products');
                    setShowAdBanner(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-plane-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">Maldives Luxury</p>
                      <p className="text-white/80 text-xs">Earn $1,500 Commission!</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  navigate('/products');
                  setShowAdBanner(false);
                }}
                className="w-full py-3 bg-white text-primary rounded-lg font-bold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Shop Now <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 text-white py-20">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="relative px-6 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Blog</h1>
              <p className="text-xl text-gray-300">
                Insights, tips, and stories from the world of digital business
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="px-6 lg:px-12 py-16">
          <div className="max-w-7xl mx-auto">
            {/* Categories */}
            <div className="flex flex-wrap gap-3 mb-12 justify-center">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedCategory === cat
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Social Share Section */}
            <div className="flex justify-center mb-12">
              <div className="bg-gray-50 px-6 py-4 rounded-lg">
                <SocialShare 
                  title="Digital Business Blog - Latest Insights & Tips"
                  description="Discover the latest insights, tips, and stories from the world of digital business"
                />
              </div>
            </div>

            {/* Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow group"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.readTime}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        <img
                          src={post.author.avatar}
                          alt={post.author.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {post.author.name}
                          </p>
                          <p className="text-xs text-gray-500">{post.author.role}</p>
                        </div>
                      </div>

                      <Link
                        to={`/blog/${post.id}`}
                        className="text-primary hover:text-primary-dark font-medium text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap"
                      >
                        Read More
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-article-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg">No posts found in this category</p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="bg-gradient-to-br from-primary to-primary-dark text-white py-16 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Subscribe to Our Newsletter
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get the latest articles and insights delivered to your inbox
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button
                type="submit"
                className="px-8 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}